import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import MainButton from "amnPos/generalComponent/MainButton";
import SupervisorPinCode from "components/SupervisorPinCode/SupervisorPinCode";
import { MessageDialogType } from "constants/messageDialogType";
import useTranslate from "hooks/useTranslate";
import IChildrenProps from "models/common-props/IChildrenProps";
import React, { useCallback, useMemo } from "react";
import { Suspense, createContext, useState } from "react";
import Button from "components/Button";
import { NIL, v4 as uuid4 } from "uuid";
import { useThemeColors } from "theme";
import { useAppSelector } from "app/hooks";
import { buildTestId } from "helpers/testId";

const MessageDialogIcon = React.lazy(
  () => import("components/icons/MessageDialogIcon"),
);

/**
 * DOM contract for the single app-wide message dialog: the paper is
 * `message-dialog`, its heading `message-dialog-title`, its body
 * `message-dialog-description`, and the buttons `message-dialog-ok`,
 * `message-dialog-back` and `message-dialog-close`. `data-message-type`
 * mirrors {@link MessageDialogType} so a spec can assert severity without
 * reading translated copy. The dialog renders in a portal at the document
 * root, so scope to the paper.
 */
const TEST_ID = "message-dialog";

// Interface representing a dialog in the queue
interface DialogInfo {
  id: string;
  messageHeader: string;
  messageDescription: string;
  type: MessageDialogType;
  buttonText: string;
  afterClose?: Function | null;
  // For ActionMessageInfo type
  backButtonText?: string;
  okButtonText?: string;
  handleBackButton?: () => void;
  handleOkButton?: (info: any) => void;
  closeOnly?: boolean;
}

interface MessageDialogContextType {
  setMessageInfo: (
    messageHeader: string,
    messageDescription: string,
    messageType: MessageDialogType,
    bottonText?: string,
  ) => void;

  setErrorMessageInfo: (
    messageHeader: string,
    messageDescription: string,
    bottonText?: string,
    afterCloseAction?: any,
  ) => void;

  setActionCloseMessageInfo: (
    messageHeader: string,
    messageDescription: string,
    buttonText?: string,
    afterCloseAction?: Function | null,
  ) => void;

  setMessageActionInfo: (
    messageHeader: string,
    messageDescription: string,
    backButtonText: string,
    okButtonText: string,
    type: MessageDialogType,
    onBackButtonClick: () => void,
    onOkButtonClick: (info: any) => void,
  ) => void;

  closeDialog: (dialogId?: string) => void;
}

const MessageDialogContext = createContext<MessageDialogContextType>({
  setMessageInfo: (
    messageHeader: string,
    messageDescription: string,
    messageType: MessageDialogType,
    bottonText?: string,
  ) => {},

  setErrorMessageInfo: (
    messageHeader: string,
    messageDescription: string,
    bottonText?: string,
    afterCloseAction?: any,
  ) => {},

  setActionCloseMessageInfo: (
    messageHeader: string,
    messageDescription: string,
    buttonText?: string,
    afterCloseAction?: Function | null,
  ) => {},

  setMessageActionInfo: (
    messageHeader: string,
    messageDescription: string,
    backButtonText: string,
    okButtonText: string,
    type: MessageDialogType,
    onBackButtonClick: () => void,
    onOkButtonClick: (info: any) => void,
  ) => {},

  closeDialog: (dialogId?: string) => {},
});

export function MessageDialogContextProvider({ children }: IChildrenProps) {
  const { buttonTrans } = useTranslate("");
  // Queue of dialogs - each dialog has its own independent state
  const [dialogs, setDialogs] = useState<DialogInfo[]>([]);
  const [additionalinfo, setAdditionalInfo] = useState<any>();
  const colors = useThemeColors();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const posSettings = useAppSelector((store) => store.posSetting);
  // Get the current (topmost) dialog to display
  const currentDialog = dialogs.length > 0 ? dialogs[dialogs.length - 1] : null;

  // Close a specific dialog by ID, or close the current one if no ID provided
  const closeDialog = useCallback((dialogId?: string) => {
    setDialogs((prev) => {
      if (dialogId) {
        // Close specific dialog by ID
        const dialogToClose = prev.find((d) => d.id === dialogId);
        if (dialogToClose?.afterClose) {
          dialogToClose.afterClose();
        }
        return prev.filter((d) => d.id !== dialogId);
      } else {
        // Close the topmost dialog
        if (prev.length > 0) {
          const topDialog = prev[prev.length - 1];
          if (topDialog.afterClose) {
            topDialog.afterClose();
          }
          return prev.slice(0, -1);
        }
        return prev;
      }
    });
    setAdditionalInfo(undefined);
  }, []);

  function handleClose(event: any, reason: any) {
    if (reason && reason === "backdropClick") return;
    closeDialog();
  }

  const setMessageInfo = useCallback(
    (
      messageHeader: string,
      messageDescription: string,
      messageType: MessageDialogType,
      bottonText?: string,
    ) => {
      const newDialog: DialogInfo = {
        id: uuid4(),
        messageHeader,
        messageDescription,
        type: messageType,
        buttonText: bottonText ? buttonTrans(bottonText) : buttonTrans("ok"),
      };
      setDialogs((prev) => [...prev, newDialog]);
    },
    [buttonTrans],
  );

  const setMessageActionInfo = useCallback(
    (
      messageHeader: string,
      messageDescription: string,
      backButtonText: string,
      okButtonText: string,
      type: MessageDialogType,
      onBackButtonClick: () => void,
      onOkButtonClick: (info: any) => void,
    ) => {
      const newDialog: DialogInfo = {
        id: uuid4(),
        messageHeader,
        messageDescription,
        type,
        buttonText: buttonTrans("ok"),
        backButtonText,
        okButtonText,
        handleBackButton: onBackButtonClick,
        handleOkButton: onOkButtonClick,
      };
      setDialogs((prev) => [...prev, newDialog]);
    },
    [buttonTrans],
  );

  const setErrorMessageInfo = useCallback(
    (
      messageHeader: string,
      messageDescription: string,
      bottonText?: string,
      afterCloseAction?: Function | null,
    ) => {
      const newDialog: DialogInfo = {
        id: uuid4(),
        messageHeader,
        messageDescription,
        type: MessageDialogType.Error,
        buttonText: bottonText ? buttonTrans(bottonText) : buttonTrans("ok"),
        backButtonText: bottonText
          ? buttonTrans(bottonText)
          : buttonTrans("close"),
        afterClose: afterCloseAction,
      };
      setDialogs((prev) => [...prev, newDialog]);
    },
    [buttonTrans],
  );

  const setActionCloseMessageInfo = useCallback(
    (
      messageHeader: string,
      messageDescription: string,
      buttonText = "close",
      afterCloseAction?: Function | null,
    ) => {
      const newDialog: DialogInfo = {
        id: uuid4(),
        messageHeader,
        messageDescription,
        type: MessageDialogType.ActionMessageInfo,
        buttonText: buttonTrans(buttonText),
        backButtonText: buttonTrans(buttonText),
        afterClose: afterCloseAction,
        closeOnly: true,
      };
      setDialogs((prev) => [...prev, newDialog]);
    },
    [buttonTrans],
  );

  const context = useMemo(
    () => ({
      setMessageInfo,
      setErrorMessageInfo,
      setActionCloseMessageInfo,
      setMessageActionInfo,
      closeDialog,
    }),
    [
      setMessageInfo,
      setErrorMessageInfo,
      setActionCloseMessageInfo,
      setMessageActionInfo,
      closeDialog,
    ],
  );

  return (
    <MessageDialogContext.Provider value={context}>
      {children}
      <Dialog
        open={currentDialog !== null}
        onClose={handleClose}
        slotProps={{
          paper: {
            "data-testid": TEST_ID,
            "data-message-type": currentDialog
              ? MessageDialogType[currentDialog.type]
              : undefined,
          } as any,
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "500px",
            },
          },
        }}
      >
        {currentDialog && (
          <>
            <Stack
              sx={{
                alignItems: "center",
                position: "relative"
              }}>
              <DialogTitle
                id="message-dialog-title"
                style={{
                  borderRadius: "50%",
                  boxShadow: `0 4px 12px 0 rgba(0, 0, 0, 0.10)`,
                  position: "absolute",
                  top: "-50px",
                  padding: "10px",
                  background: colors.background.secondary,
                }}
              >
                <Suspense fallback={<div>Loading ...</div>}>
                  <MessageDialogIcon
                    iconType={
                      currentDialog.type === MessageDialogType.ActionMessageInfo
                        ? MessageDialogType.Error
                        : currentDialog.type
                    }
                    isDark={isDark}
                  />
                </Suspense>
              </DialogTitle>
            </Stack>

            <Stack sx={{
              alignItems: "center"
            }}>
              <DialogContent
                sx={{
                  mt: "60px",
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    marginBottom: "3px",
                  }}
                  data-testid={buildTestId(TEST_ID, "title")}
                >
                  {currentDialog.messageHeader}
                </Typography>

                {currentDialog.messageDescription && (
                  <Typography
                    id="message-dialog-description"
                    dangerouslySetInnerHTML={{
                      __html: currentDialog.messageDescription,
                    }}
                    sx={{
                      paddingTop: "5px",
                      fontSize: "15px",
                      fontWeight: "400",
                      textAlign: "center",
                      color: isDark ? "#A5A5A5" : colors.text.secondary,
                    }}
                    data-testid={buildTestId(TEST_ID, "description")}
                  ></Typography>
                )}
              </DialogContent>
              {currentDialog.type === MessageDialogType.ActionMessageInfo &&
                !currentDialog.closeOnly && (
                  <SupervisorPinCode
                    HandleOnCompletePinCode={(supervisor) => {
                      setAdditionalInfo(supervisor);
                    }}
                    HandleOnChangePinCode={() => {
                      setAdditionalInfo(null);
                    }}
                    HandleOnManyWrongAttempts={() => {
                      currentDialog.handleBackButton?.();
                      closeDialog(currentDialog.id);
                    }}
                    posId={posSettings.id}
                  />
                )}

              <DialogActions sx={{ marginBottom: "24px", paddingTop: "12px" }}>
                {currentDialog.type !== MessageDialogType.ActionMessageInfo && (
                  <Button
                    sx={{
                      backgroundColor: colors.brand.primary,
                      color: colors.background.primary,
                    }}
                    onClick={() => handleClose("", "")}
                    data-testid={buildTestId(TEST_ID, "ok")}
                  >
                    {currentDialog.buttonText}
                  </Button>
                )}
                {currentDialog.type === MessageDialogType.ActionMessageInfo &&
                  currentDialog.closeOnly && (
                    <Button
                      variant={"cancel"}
                      onClick={() => {
                        currentDialog.afterClose?.();
                        closeDialog(currentDialog.id);
                      }}
                      data-testid={buildTestId(TEST_ID, "close")}
                    >
                      {currentDialog.backButtonText}
                    </Button>
                  )}
                {currentDialog.type === MessageDialogType.ActionMessageInfo &&
                  !currentDialog.closeOnly && (
                    <Button
                      variant={"cancel"}
                      onClick={() => {
                        currentDialog.handleBackButton?.();
                        closeDialog(currentDialog.id);
                      }}
                      data-testid={buildTestId(TEST_ID, "back")}
                    >
                      {currentDialog.backButtonText}
                    </Button>
                  )}
                {currentDialog.type === MessageDialogType.ActionMessageInfo &&
                  !currentDialog.closeOnly && (
                    <Button
                      variant={"primary"}
                      onClick={() => {
                        currentDialog.handleOkButton?.(additionalinfo);
                        closeDialog(currentDialog.id);
                      }}
                      data-testid={buildTestId(TEST_ID, "ok")}
                      disabled={
                        !additionalinfo || additionalinfo?.supervisorId === NIL
                      }
                    >
                      {currentDialog.okButtonText}
                    </Button>
                  )}
              </DialogActions>
            </Stack>
          </>
        )}
      </Dialog>
    </MessageDialogContext.Provider>
  );
}
export default MessageDialogContext;
